/* DocumentLedger — /admin/ledger
   Phase 1 redesign: stat cards, category + source filters, bulk category assign,
   soft-delete, mark-for-printing toggle, reconciliation status.
   Phase 2: direction picker, Categories management tab. */
import { useState } from 'react';
import css from '../AdminPage.module.css';
import {
  useLedgerDocuments,
  useBulkUpdateLedgerStatus,
  useBulkDownloadDocuments,
  useLedgerStats,
  useDocumentCategories,
  useBulkAssignCategory,
  useSoftDeleteDocument,
  useMarkForPrinting,
  useTriggerReconciliation,
  useReconciliationStatus,
  useDocumentEvents,
  useUpdateDirection,
  useCreateDocumentCategory,
  useUpdateDocumentCategory,
  useDeleteDocumentCategory,
} from '../../../hooks/useAdmin';
import { useToast } from '../../../hooks/useToast';
import DatePicker from '../../../components/ui/DatePicker/DatePicker';
import type { LedgerFilters, DocumentEvent, DocumentCategory } from '../../../types/admin';

const CASE_TYPES = ['Housing Disrepair', 'Financial Services', 'DSAR', 'Personal Injury'];
const DOC_TYPES = [
  'Letter of Claim',
  'LBA',
  'Court Bundle',
  'Witness Statement',
  'Claim Form',
  'Schedule of Loss',
];
const SOURCES = ['generated', 'uploaded', 'received', 'pre-ledger', 'direct-s3'];
const ROWS_PER_PAGE = 25;

export default function DocumentLedger() {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<'ledger' | 'categories'>('ledger');
  const bulkUpdateStatus = useBulkUpdateLedgerStatus();
  const bulkDownload = useBulkDownloadDocuments();
  const bulkAssignCategory = useBulkAssignCategory();
  const softDelete = useSoftDeleteDocument();
  const markForPrinting = useMarkForPrinting();
  const triggerReconciliation = useTriggerReconciliation();
  const updateDirection = useUpdateDirection();

  const { data: stats } = useLedgerStats();
  const { data: categories = [] } = useDocumentCategories();
  const { data: reconciliationRuns = [] } = useReconciliationStatus();

  const [filters, setFilters] = useState<LedgerFilters>({ limit: ROWS_PER_PAGE });
  const [searchInput, setSearchInput] = useState('');
  const { data, isLoading } = useLedgerDocuments(filters);
  const documents = data?.documents ?? [];
  const total = data?.total ?? 0;
  const hasMore = data?.hasMore ?? false;

  const [selected, setSelected] = useState<Set<number | string>>(new Set());
  const [assignCategoryId, setAssignCategoryId] = useState<number | ''>('');
  const [eventsDocId, setEventsDocId] = useState<string | number | null>(null);

  function setFilter(key: keyof LedgerFilters, value: string | boolean | undefined) {
    setFilters((prev) => ({ ...prev, [key]: value || undefined, page: 1 }));
    setSelected(new Set());
  }

  function handleSearch() {
    setFilters((prev) => ({ ...prev, search: searchInput || undefined, page: 1 }));
    setSelected(new Set());
  }

  function toggleSelect(id: number | string) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (selected.size === documents.length) setSelected(new Set());
    else setSelected(new Set(documents.map((d) => d.id)));
  }

  async function bulkAction(newStatus: string) {
    if (selected.size === 0) return;
    try {
      await bulkUpdateStatus.mutateAsync({ documentIds: [...selected], newStatus });
      showToast(`${selected.size} document(s) marked as ${newStatus}`, 'success');
      setSelected(new Set());
    } catch {
      showToast('Bulk update failed', 'error');
    }
  }

  async function handleBulkDownload() {
    if (selected.size === 0) return;
    try {
      await bulkDownload.mutateAsync([...selected]);
      showToast('Download started', 'success');
    } catch {
      showToast('Download failed', 'error');
    }
  }

  async function handleBulkAssignCategory() {
    if (selected.size === 0 || !assignCategoryId) return;
    try {
      const result = await bulkAssignCategory.mutateAsync({
        documentIds: [...selected],
        categoryId: assignCategoryId as number,
      });
      showToast(`Category assigned to ${result.updated} document(s)`, 'success');
      setSelected(new Set());
      setAssignCategoryId('');
    } catch {
      showToast('Category assignment failed', 'error');
    }
  }

  async function handleSoftDelete(id: number | string) {
    if (!confirm('Soft-delete this document from the ledger?')) return;
    try {
      await softDelete.mutateAsync({ id, reason: 'manual' });
      showToast('Document soft-deleted', 'success');
    } catch {
      showToast('Delete failed', 'error');
    }
  }

  async function handleTogglePrint(id: number | string, current: boolean) {
    try {
      await markForPrinting.mutateAsync({ id, mark: !current });
      showToast(!current ? 'Marked for printing' : 'Removed from print queue', 'success');
    } catch {
      showToast('Update failed', 'error');
    }
  }

  async function handleUpdateDirection(id: number | string, direction: string) {
    try {
      await updateDirection.mutateAsync({ documentId: String(id), direction });
    } catch {
      showToast('Failed to update direction', 'error');
    }
  }

  async function handleReconcile() {
    try {
      await triggerReconciliation.mutateAsync();
      showToast('Reconciliation started — check status below', 'success');
    } catch {
      showToast('Failed to start reconciliation', 'error');
    }
  }

  function badgeFor(s: string) {
    switch (s) {
      case 'printed':
        return css.badgeSuccess;
      case 'pending':
        return css.badgeWarning;
      case 'archived':
        return css.badgeNeutral;
      default:
        return css.badgeNeutral;
    }
  }

  function loadMore() {
    if (data?.nextPageToken) {
      setFilters((prev) => ({ ...prev, page: (prev.page ?? 1) + 1 }));
    }
  }

  const totalPages = Math.ceil(total / (filters.limit || ROWS_PER_PAGE));
  const latestRun = reconciliationRuns[0];

  if (isLoading) {
    return (
      <div className={css.spinner}>
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
      </div>
    );
  }

  return (
    <div className={css.section}>
      <div className={css.sectionHeader}>
        <div>
          <h2 className={css.sectionTitle}>📒 Document Ledger</h2>
          <p className={css.sectionSubtitle}>
            {activeTab === 'ledger'
              ? `${total} document${total !== 1 ? 's' : ''}`
              : 'Category management'}
          </p>
        </div>
        {activeTab === 'ledger' && (
          <button
            className={css.btnSecondary}
            onClick={handleReconcile}
            disabled={triggerReconciliation.isPending}
            title="Run S3 ↔ ledger reconciliation"
          >
            🔄 {triggerReconciliation.isPending ? 'Running…' : 'Reconcile'}
          </button>
        )}
      </div>

      {/* ── Tab bar ── */}
      <div
        style={{
          display: 'flex',
          gap: 0,
          borderBottom: '2px solid var(--border-primary)',
          marginBottom: 20,
        }}
      >
        {(
          [
            ['ledger', '📋 Ledger'],
            ['categories', '🏷️ Categories'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            style={{
              padding: '8px 20px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: 'var(--font-size-sm)',
              fontWeight: activeTab === id ? 700 : 400,
              color: activeTab === id ? 'var(--primary-600)' : 'var(--text-secondary)',
              borderBottom:
                activeTab === id ? '2px solid var(--primary-600)' : '2px solid transparent',
              marginBottom: -2,
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'categories' ? (
        <CategoriesTab />
      ) : (
        <>
          {/* ── Stat cards ── */}
          {stats && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                gap: 12,
                marginBottom: 20,
              }}
            >
              <StatCard label="Total Documents" value={stats.total} icon="📄" />
              <StatCard label="Mark for Print" value={stats.markForPrintingCount} icon="🖨️" />
              <StatCard label="Soft-Deleted" value={stats.softDeletedCount} icon="🗑️" />
              <StatCard
                label="Last Drift"
                value={stats.lastReconciliation ? `${stats.lastReconciliation.drift_count}` : '—'}
                icon={stats.lastReconciliation?.drift_count ? '⚠️' : '✅'}
                subtext={
                  stats.lastReconciliation
                    ? new Date(stats.lastReconciliation.run_at).toLocaleDateString('en-GB')
                    : 'No run yet'
                }
              />
            </div>
          )}

          {/* ── Filters ── */}
          <div className={css.toolbar} style={{ marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
            <div className={css.searchWrapper}>
              <span className={css.searchIcon}>🔍</span>
              <input
                className={css.searchInput}
                placeholder="Search case ref..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <button
              className={css.btnSecondary}
              onClick={handleSearch}
              style={{ padding: '4px 12px' }}
            >
              Search
            </button>
            <select
              className={css.formSelect}
              value={filters.status || ''}
              onChange={(e) => setFilter('status', e.target.value)}
              style={{ maxWidth: 130 }}
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="printed">Printed</option>
              <option value="archived">Archived</option>
            </select>
            <select
              className={css.formSelect}
              value={filters.source || ''}
              onChange={(e) => setFilter('source', e.target.value)}
              style={{ maxWidth: 130 }}
            >
              <option value="">All Sources</option>
              {SOURCES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <select
              className={css.formSelect}
              value={filters.categoryId ?? ''}
              onChange={(e) =>
                setFilter(
                  'categoryId',
                  e.target.value ? (Number(e.target.value) as unknown as string) : '',
                )
              }
              style={{ maxWidth: 160 }}
            >
              <option value="">All Categories</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <select
              className={css.formSelect}
              value={filters.caseType || ''}
              onChange={(e) => setFilter('caseType', e.target.value)}
              style={{ maxWidth: 180 }}
            >
              <option value="">All Case Types</option>
              {CASE_TYPES.map((ct) => (
                <option key={ct} value={ct}>
                  {ct}
                </option>
              ))}
            </select>
            <select
              className={css.formSelect}
              value={filters.documentType || ''}
              onChange={(e) => setFilter('documentType', e.target.value)}
              style={{ maxWidth: 180 }}
            >
              <option value="">All Doc Types</option>
              {DOC_TYPES.map((dt) => (
                <option key={dt} value={dt}>
                  {dt}
                </option>
              ))}
            </select>
            <DatePicker
              value={filters.dateFrom || ''}
              onChange={(v) => setFilter('dateFrom', v)}
              className={css.formInput}
              style={{ maxWidth: 140 }}
            />
            <span style={{ color: 'var(--gray-400)', fontSize: 'var(--font-size-xs)' }}>to</span>
            <DatePicker
              value={filters.dateTo || ''}
              onChange={(v) => setFilter('dateTo', v)}
              className={css.formInput}
              style={{ maxWidth: 140 }}
            />
          </div>

          {/* ── Bulk action bar ── */}
          {selected.size > 0 && (
            <div
              style={{
                display: 'flex',
                gap: 8,
                marginBottom: 12,
                padding: '8px 12px',
                background: 'var(--primary-50, #eff6ff)',
                borderRadius: 'var(--radius-md)',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>
                {selected.size} selected
              </span>
              <button
                className={`${css.btnPrimary} ${css.btnSmall}`}
                onClick={() => bulkAction('printed')}
                disabled={bulkUpdateStatus.isPending}
              >
                ✓ Mark Printed
              </button>
              <button
                className={`${css.btnSecondary} ${css.btnSmall}`}
                onClick={() => bulkAction('archived')}
                disabled={bulkUpdateStatus.isPending}
              >
                📦 Archive
              </button>
              <button
                className={`${css.btnSecondary} ${css.btnSmall}`}
                onClick={handleBulkDownload}
                disabled={bulkDownload.isPending}
              >
                ⬇ Download
              </button>
              <select
                className={css.formSelect}
                value={assignCategoryId}
                onChange={(e) => setAssignCategoryId(e.target.value ? Number(e.target.value) : '')}
                style={{ maxWidth: 160, fontSize: 'var(--font-size-xs)' }}
              >
                <option value="">Assign category…</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              {assignCategoryId && (
                <button
                  className={`${css.btnPrimary} ${css.btnSmall}`}
                  onClick={handleBulkAssignCategory}
                  disabled={bulkAssignCategory.isPending}
                >
                  🏷️ Assign
                </button>
              )}
              <button
                className={`${css.btnSecondary} ${css.btnSmall}`}
                onClick={() => setSelected(new Set())}
                style={{ marginLeft: 'auto' }}
              >
                Clear
              </button>
            </div>
          )}

          {documents.length === 0 ? (
            <div className={css.emptyState}>
              <div className={css.emptyIcon}>📒</div>
              <p className={css.emptyMessage}>No documents found</p>
            </div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table className={css.table} style={{ minWidth: 1100 }}>
                  <thead>
                    <tr>
                      <th style={{ width: 32 }}>
                        <input
                          type="checkbox"
                          checked={selected.size === documents.length && documents.length > 0}
                          onChange={toggleAll}
                        />
                      </th>
                      <th>Case Ref</th>
                      <th>Document</th>
                      <th>Category</th>
                      <th>Direction</th>
                      <th>Source</th>
                      <th>Status</th>
                      <th>🖨️</th>
                      <th>Generated</th>
                      <th>By</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {documents.map((d, idx) => (
                      <tr key={d.id ?? `ledger-${idx}`}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selected.has(d.id)}
                            onChange={() => toggleSelect(d.id)}
                          />
                        </td>
                        <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {d.case_ref || '—'}
                        </td>
                        <td style={{ fontSize: 'var(--font-size-xs)' }}>
                          {d.document_name || d.document_type || '—'}
                        </td>
                        <td>
                          {d.category_name ? (
                            <span
                              style={{
                                background: d.category_colour || '#e5e7eb',
                                color: '#1f2937',
                                padding: '1px 8px',
                                borderRadius: 10,
                                fontSize: '0.7rem',
                                fontWeight: 600,
                              }}
                            >
                              {d.category_name}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--gray-400)', fontSize: '0.7rem' }}>—</span>
                          )}
                        </td>
                        <td>
                          <select
                            className={css.formSelect}
                            value={
                              ((d as unknown as Record<string, unknown>).direction as string) || ''
                            }
                            onChange={(e) => handleUpdateDirection(d.id, e.target.value)}
                            style={{ fontSize: '0.7rem', padding: '2px 4px', maxWidth: 140 }}
                            title="Document direction"
                          >
                            <option value="">—</option>
                            <option value="inbound">Inbound</option>
                            <option value="outbound">Outbound</option>
                            <option value="internal">Internal</option>
                            <option value="received_from_client">From Client</option>
                            <option value="received_from_opponent">From Opponent</option>
                            <option value="received_from_court">From Court</option>
                            <option value="filed_with_court">Filed w/ Court</option>
                          </select>
                        </td>
                        <td>
                          <span className={css.badgeNeutral} style={{ fontSize: '0.7rem' }}>
                            {d.source || '—'}
                          </span>
                        </td>
                        <td>
                          <span className={badgeFor(d.status)}>{d.status}</span>
                        </td>
                        <td>
                          <input
                            type="checkbox"
                            checked={!!d.mark_for_printing}
                            onChange={() => handleTogglePrint(d.id, !!d.mark_for_printing)}
                            title="Mark for printing"
                          />
                        </td>
                        <td style={{ fontSize: 'var(--font-size-xs)', whiteSpace: 'nowrap' }}>
                          {d.generated_at ? new Date(d.generated_at).toLocaleString() : '—'}
                        </td>
                        <td style={{ fontSize: 'var(--font-size-xs)' }}>{d.generated_by || '—'}</td>
                        <td>
                          <button
                            onClick={() => handleSoftDelete(d.id)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: 'var(--error-500)',
                              fontSize: '0.8rem',
                              padding: '2px 4px',
                            }}
                            title="Soft-delete"
                          >
                            🗑️
                          </button>
                          <button
                            onClick={() => setEventsDocId(d.id)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: 'var(--primary-600)',
                              fontSize: '0.8rem',
                              padding: '2px 4px',
                            }}
                            title="View event timeline"
                          >
                            📋
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className={css.pagination}>
                <button
                  className={css.pageBtn}
                  disabled={(filters.page ?? 1) <= 1}
                  onClick={() =>
                    setFilters((p) => ({ ...p, page: Math.max(1, (p.page ?? 1) - 1) }))
                  }
                >
                  ‹ Prev
                </button>
                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-500)' }}>
                  Page {filters.page ?? 1}
                  {totalPages > 1 ? ` of ${totalPages}` : ''} ({total} total)
                </span>
                <button
                  className={css.pageBtn}
                  disabled={!hasMore && (filters.page ?? 1) >= totalPages}
                  onClick={loadMore}
                >
                  Next ›
                </button>
              </div>
            </>
          )}
        </>
      )}

      {/* ── Reconciliation history ── */}
      {activeTab === 'ledger' && latestRun && (
        <div style={{ marginTop: 24 }}>
          <h3 style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, marginBottom: 8 }}>
            Last reconciliation
          </h3>
          <div
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--text-secondary)',
              display: 'flex',
              gap: 16,
              flexWrap: 'wrap',
            }}
          >
            <span>{new Date(latestRun.run_at).toLocaleString()}</span>
            <span
              className={
                latestRun.status === 'completed'
                  ? css.badgeSuccess
                  : latestRun.status === 'failed'
                    ? css.badgeError
                    : css.badgeNeutral
              }
            >
              {latestRun.status}
            </span>
            {latestRun.drift_count != null && <span>{latestRun.drift_count} drift</span>}
            {latestRun.missing_in_ledger != null && (
              <span>{latestRun.missing_in_ledger} missing in ledger</span>
            )}
            {latestRun.orphaned_in_ledger != null && (
              <span>{latestRun.orphaned_in_ledger} orphaned</span>
            )}
          </div>
        </div>
      )}

      {/* ── Document events drawer ── */}
      {eventsDocId != null && (
        <EventsDrawer documentId={eventsDocId} onClose={() => setEventsDocId(null)} />
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  icon,
  subtext,
}: {
  label: string;
  value: number | string;
  icon: string;
  subtext?: string;
}) {
  return (
    <div
      style={{
        border: '1px solid var(--border-primary)',
        borderRadius: 'var(--radius-lg)',
        padding: '12px 16px',
        background: 'var(--surface-primary)',
      }}
    >
      <div style={{ fontSize: '1.4rem', marginBottom: 4 }}>{icon}</div>
      <div
        style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, color: 'var(--text-primary)' }}
      >
        {value}
      </div>
      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-tertiary)', marginTop: 2 }}>
        {label}
      </div>
      {subtext && (
        <div style={{ fontSize: '0.68rem', color: 'var(--text-tertiary)', marginTop: 2 }}>
          {subtext}
        </div>
      )}
    </div>
  );
}

function EventsDrawer({
  documentId,
  onClose,
}: {
  documentId: string | number;
  onClose: () => void;
}) {
  const { data: events = [], isLoading } = useDocumentEvents(documentId);
  return (
    <div
      style={{
        position: 'fixed',
        right: 0,
        top: 0,
        bottom: 0,
        width: 420,
        background: 'var(--surface-primary)',
        boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        borderLeft: '1px solid var(--border-primary)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '16px 20px',
          borderBottom: '1px solid var(--border-primary)',
        }}
      >
        <h3 style={{ flex: 1, margin: 0, fontSize: 'var(--font-size-base)', fontWeight: 700 }}>
          Document Timeline
        </h3>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: '1.2rem',
            color: 'var(--text-secondary)',
          }}
        >
          ✕
        </button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
        {isLoading ? (
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)' }}>
            Loading events…
          </p>
        ) : events.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)' }}>
            No events recorded for this document.
          </p>
        ) : (
          <ol style={{ listStyle: 'none', padding: 0, margin: 0, position: 'relative' }}>
            <div
              style={{
                position: 'absolute',
                left: 11,
                top: 8,
                bottom: 8,
                width: 2,
                background: 'var(--border-primary)',
              }}
            />
            {events.map((ev: DocumentEvent, i: number) => (
              <li
                key={ev.id ?? i}
                style={{ display: 'flex', gap: 12, marginBottom: 16, position: 'relative' }}
              >
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    background: 'var(--primary-100)',
                    border: '2px solid var(--primary-400)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.6rem',
                    flexShrink: 0,
                    zIndex: 1,
                  }}
                >
                  {eventIcon(ev.event_type)}
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--text-primary)',
                    }}
                  >
                    {formatEventType(ev.event_type)}
                  </div>
                  <div style={{ fontSize: '0.68rem', color: 'var(--text-tertiary)', marginTop: 1 }}>
                    {new Date(ev.event_at).toLocaleString()} · {ev.actor ?? 'System'}
                  </div>
                  {ev.new_value != null && (
                    <div
                      style={{
                        marginTop: 4,
                        fontSize: '0.68rem',
                        background: 'var(--surface-secondary)',
                        padding: '3px 6px',
                        borderRadius: 4,
                        color: 'var(--text-secondary)',
                        fontFamily: 'monospace',
                        overflowX: 'auto',
                      }}
                    >
                      {typeof ev.new_value === 'string'
                        ? ev.new_value
                        : JSON.stringify(ev.new_value as object, null, 2)}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

function eventIcon(type: string) {
  if (type.includes('creat') || type.includes('upload')) return '⬆';
  if (type.includes('delet')) return '🗑';
  if (type.includes('print')) return '🖨';
  if (type.includes('status')) return '🔄';
  if (type.includes('direction')) return '↔';
  if (type.includes('category')) return '🏷';
  return '●';
}

function formatEventType(type: string) {
  return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── System category descriptions ──────────────────────────────────────────────
const SYSTEM_CATEGORY_DESCRIPTIONS: Record<string, string> = {
  'court-bundle': 'Consolidated document bundle prepared for court submission.',
  correspondence: 'Letters, emails, and written communications with any party.',
  evidence: 'Supporting evidence including medical records, photographs, and expert reports.',
  pleadings: 'Formal court documents: claim forms, defences, replies, and part 18 requests.',
  financial: 'Financial documents: schedules of loss, invoices, costs schedules, and fee records.',
  identification:
    'Identity verification documents: passports, driving licences, and utility bills.',
  instructions: 'Client instructions, terms of engagement, and retainer documentation.',
  'expert-reports': 'Reports from independent experts, surveyors, and medical professionals.',
  'witness-statements': 'Written witness statements prepared for disclosure or court use.',
  settlement: 'Settlement offers, Calderbank letters, and consent orders.',
};

// ── Categories Tab ────────────────────────────────────────────────────────────

type CategoryFormState = {
  name: string;
  slug: string;
  description: string;
  colour: string;
  sort_order: string;
};

const EMPTY_CAT_FORM: CategoryFormState = {
  name: '',
  slug: '',
  description: '',
  colour: '#6B7280',
  sort_order: '0',
};

function toSlug(name: string) {
  return name
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

function CategoriesTab() {
  const { showToast } = useToast();
  const { data: categories = [], isLoading } = useDocumentCategories();
  const createCategory = useCreateDocumentCategory();
  const updateCategory = useUpdateDocumentCategory();
  const deleteCategory = useDeleteDocumentCategory();

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<Partial<CategoryFormState>>({});
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState<CategoryFormState>(EMPTY_CAT_FORM);

  function startEdit(cat: DocumentCategory) {
    setEditingId(cat.id);
    setEditForm({
      description: cat.description ?? '',
      colour: cat.colour,
      sort_order: String(cat.sort_order),
    });
  }

  function cancelEdit() {
    setEditingId(null);
    setEditForm({});
  }

  async function saveEdit(cat: DocumentCategory) {
    try {
      await updateCategory.mutateAsync({
        id: cat.id,
        description: editForm.description,
        colour: editForm.colour,
        sort_order: editForm.sort_order ? parseInt(editForm.sort_order, 10) : undefined,
        ...(!cat.is_system && editForm.name ? { name: editForm.name } : {}),
      });
      showToast('Category updated', 'success');
      cancelEdit();
    } catch {
      showToast('Update failed', 'error');
    }
  }

  async function handleDelete(cat: DocumentCategory) {
    if (cat.is_system) return;
    if (!confirm(`Delete category "${cat.name}"? This cannot be undone.`)) return;
    try {
      await deleteCategory.mutateAsync(cat.id);
      showToast('Category deleted', 'success');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      showToast(msg ?? 'Delete failed', 'error');
    }
  }

  async function handleCreate() {
    if (!createForm.name || !createForm.slug) {
      showToast('Name and slug are required', 'error');
      return;
    }
    try {
      await createCategory.mutateAsync({
        name: createForm.name,
        slug: createForm.slug,
        description: createForm.description || undefined,
        colour: createForm.colour,
        sort_order: createForm.sort_order ? parseInt(createForm.sort_order, 10) : 0,
      });
      showToast('Category created', 'success');
      setCreateForm(EMPTY_CAT_FORM);
      setShowCreateForm(false);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      showToast(msg ?? 'Create failed', 'error');
    }
  }

  if (isLoading)
    return (
      <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)' }}>
        Loading categories…
      </p>
    );

  const system = categories.filter((c) => c.is_system);
  const custom = categories.filter((c) => !c.is_system);

  return (
    <div>
      <h3
        style={{
          fontSize: 'var(--font-size-sm)',
          fontWeight: 700,
          marginBottom: 12,
          color: 'var(--text-primary)',
        }}
      >
        System Categories
        <span
          style={{
            marginLeft: 8,
            fontSize: '0.7rem',
            fontWeight: 400,
            color: 'var(--text-tertiary)',
          }}
        >
          (names are fixed; descriptions and colour can be edited)
        </span>
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 28 }}>
        {system.map((cat) => (
          <CategoryRow
            key={cat.id}
            cat={cat}
            systemDesc={SYSTEM_CATEGORY_DESCRIPTIONS[cat.slug] ?? ''}
            editing={editingId === cat.id}
            editForm={editForm}
            onEdit={() => startEdit(cat)}
            onCancel={cancelEdit}
            onSave={() => saveEdit(cat)}
            onDelete={() => handleDelete(cat)}
            onChange={(k, v) => setEditForm((p) => ({ ...p, [k]: v }))}
            isSaving={updateCategory.isPending}
          />
        ))}
        {system.length === 0 && (
          <p style={{ color: 'var(--text-tertiary)', fontSize: 'var(--font-size-xs)' }}>
            No system categories found.
          </p>
        )}
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 12,
        }}
      >
        <h3
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 700,
            margin: 0,
            color: 'var(--text-primary)',
          }}
        >
          Custom Categories
        </h3>
        <button
          className={css.btnPrimary}
          style={{ padding: '4px 14px', fontSize: 'var(--font-size-xs)' }}
          onClick={() => setShowCreateForm(true)}
        >
          + Add Category
        </button>
      </div>

      {showCreateForm && (
        <div
          style={{
            border: '1px solid var(--primary-300)',
            borderRadius: 'var(--radius-md)',
            padding: 16,
            marginBottom: 16,
            background: 'var(--primary-50, #eff6ff)',
          }}
        >
          <h4 style={{ margin: '0 0 12px', fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>
            New Category
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label style={labelStyle}>
              Name *
              <input
                className={css.formInput}
                value={createForm.name}
                onChange={(e) =>
                  setCreateForm((p) => ({
                    ...p,
                    name: e.target.value,
                    slug: toSlug(e.target.value),
                  }))
                }
                placeholder="e.g. Client Authority"
              />
            </label>
            <label style={labelStyle}>
              Slug *
              <input
                className={css.formInput}
                value={createForm.slug}
                onChange={(e) => setCreateForm((p) => ({ ...p, slug: toSlug(e.target.value) }))}
                placeholder="auto-generated"
              />
            </label>
            <label style={{ ...labelStyle, gridColumn: '1 / -1' }}>
              Description
              <input
                className={css.formInput}
                value={createForm.description}
                onChange={(e) => setCreateForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="Optional description"
              />
            </label>
            <label style={labelStyle}>
              Colour
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="color"
                  value={createForm.colour}
                  onChange={(e) => setCreateForm((p) => ({ ...p, colour: e.target.value }))}
                  style={{
                    width: 36,
                    height: 28,
                    padding: 2,
                    border: '1px solid var(--border-primary)',
                    borderRadius: 4,
                    cursor: 'pointer',
                  }}
                />
                <span
                  style={{
                    fontSize: '0.7rem',
                    color: 'var(--text-tertiary)',
                    fontFamily: 'monospace',
                  }}
                >
                  {createForm.colour}
                </span>
              </div>
            </label>
            <label style={labelStyle}>
              Sort order
              <input
                type="number"
                className={css.formInput}
                value={createForm.sort_order}
                onChange={(e) => setCreateForm((p) => ({ ...p, sort_order: e.target.value }))}
                style={{ maxWidth: 80 }}
              />
            </label>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button
              className={css.btnPrimary}
              style={{ padding: '4px 14px', fontSize: 'var(--font-size-xs)' }}
              onClick={handleCreate}
              disabled={createCategory.isPending}
            >
              {createCategory.isPending ? 'Creating…' : 'Create'}
            </button>
            <button
              className={css.btnSecondary}
              style={{ padding: '4px 14px', fontSize: 'var(--font-size-xs)' }}
              onClick={() => {
                setShowCreateForm(false);
                setCreateForm(EMPTY_CAT_FORM);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {custom.map((cat) => (
          <CategoryRow
            key={cat.id}
            cat={cat}
            systemDesc=""
            editing={editingId === cat.id}
            editForm={editForm}
            onEdit={() => startEdit(cat)}
            onCancel={cancelEdit}
            onSave={() => saveEdit(cat)}
            onDelete={() => handleDelete(cat)}
            onChange={(k, v) => setEditForm((p) => ({ ...p, [k]: v }))}
            isSaving={updateCategory.isPending}
          />
        ))}
        {custom.length === 0 && !showCreateForm && (
          <p style={{ color: 'var(--text-tertiary)', fontSize: 'var(--font-size-xs)' }}>
            No custom categories yet. Click "+ Add Category" to create one.
          </p>
        )}
      </div>
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 500,
  color: 'var(--text-secondary)',
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

type CategoryRowProps = {
  cat: DocumentCategory;
  systemDesc: string;
  editing: boolean;
  editForm: Partial<CategoryFormState>;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  onDelete: () => void;
  onChange: (key: keyof CategoryFormState, value: string) => void;
  isSaving: boolean;
};

function CategoryRow({
  cat,
  systemDesc,
  editing,
  editForm,
  onEdit,
  onCancel,
  onSave,
  onDelete,
  onChange,
  isSaving,
}: CategoryRowProps) {
  if (editing) {
    return (
      <div
        style={{
          border: '1px solid var(--primary-300)',
          borderRadius: 'var(--radius-md)',
          padding: '10px 14px',
          background: 'var(--primary-50, #eff6ff)',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: cat.is_system ? '1fr' : '1fr 1fr',
            gap: 8,
            marginBottom: 8,
          }}
        >
          {!cat.is_system && (
            <label style={labelStyle}>
              Name
              <input
                className={css.formInput}
                value={editForm.name ?? cat.name}
                onChange={(e) => onChange('name', e.target.value)}
              />
            </label>
          )}
          <label style={labelStyle}>
            Description
            <input
              className={css.formInput}
              value={editForm.description ?? cat.description ?? systemDesc}
              onChange={(e) => onChange('description', e.target.value)}
              placeholder="Optional description"
            />
          </label>
        </div>
        <div style={{ display: 'flex', gap: 16, marginBottom: 8, alignItems: 'flex-end' }}>
          <label style={labelStyle}>
            Colour
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="color"
                value={editForm.colour ?? cat.colour}
                onChange={(e) => onChange('colour', e.target.value)}
                style={{
                  width: 36,
                  height: 28,
                  padding: 2,
                  border: '1px solid var(--border-primary)',
                  borderRadius: 4,
                  cursor: 'pointer',
                }}
              />
              <span
                style={{
                  fontSize: '0.7rem',
                  color: 'var(--text-tertiary)',
                  fontFamily: 'monospace',
                }}
              >
                {editForm.colour ?? cat.colour}
              </span>
            </div>
          </label>
          {!cat.is_system && (
            <label style={labelStyle}>
              Sort order
              <input
                type="number"
                className={css.formInput}
                value={editForm.sort_order ?? String(cat.sort_order)}
                onChange={(e) => onChange('sort_order', e.target.value)}
                style={{ maxWidth: 70 }}
              />
            </label>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            className={css.btnPrimary}
            style={{ padding: '3px 12px', fontSize: 'var(--font-size-xs)' }}
            onClick={onSave}
            disabled={isSaving}
          >
            {isSaving ? 'Saving…' : 'Save'}
          </button>
          <button
            className={css.btnSecondary}
            style={{ padding: '3px 12px', fontSize: 'var(--font-size-xs)' }}
            onClick={onCancel}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 12px',
        border: '1px solid var(--border-primary)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--surface-primary)',
      }}
    >
      <div
        style={{
          width: 20,
          height: 20,
          borderRadius: 4,
          background: cat.colour,
          border: '1px solid rgba(0,0,0,0.1)',
          flexShrink: 0,
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span
            style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              color: 'var(--text-primary)',
            }}
          >
            {cat.name}
          </span>
          {cat.is_system && (
            <span
              style={{
                fontSize: '0.65rem',
                background: 'var(--gray-100)',
                color: 'var(--gray-600)',
                padding: '1px 6px',
                borderRadius: 10,
              }}
            >
              system
            </span>
          )}
          <span
            style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', fontFamily: 'monospace' }}
          >
            {cat.slug}
          </span>
        </div>
        {(cat.description || systemDesc) && (
          <div style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', marginTop: 1 }}>
            {cat.description || systemDesc}
          </div>
        )}
      </div>
      <span
        style={{
          fontSize: '0.7rem',
          color: 'var(--text-tertiary)',
          minWidth: 30,
          textAlign: 'right',
        }}
      >
        #{cat.sort_order}
      </span>
      <button
        onClick={onEdit}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--primary-600)',
          fontSize: '0.8rem',
          padding: '2px 4px',
        }}
        title="Edit"
      >
        ✏️
      </button>
      {!cat.is_system && (
        <button
          onClick={onDelete}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--error-500)',
            fontSize: '0.8rem',
            padding: '2px 4px',
          }}
          title="Delete"
        >
          🗑️
        </button>
      )}
    </div>
  );
}
